import { useEffect, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";

import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";
import { useSocket } from "@/contexts/SocketContext";
import { useAuth } from "@/hooks/useAuth";
import { alert } from "@/contexts/AlertModalContext";

import { TESTES_CATALOGO } from "@/config/testesModulo";
import type {
  ItemTesteResultado,
  StatusTeste,
  TesteModuloRegistro,
} from "@/types/teste-modulo";
import {
  buscarHistoricoTestes,
  salvarTesteModulo,
} from "@/services/teste_modulo_api";

import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  Cpu,
  History,
  Lightbulb,
  Loader2,
  Play,
  RefreshCw,
  Save,
  Square,
  User,
  Wifi,
  X,
  XCircle,
} from "lucide-react";

// ---- ZPL da etiqueta de teste (mesmo usado no card do módulo) ----
const ZPL_TESTE =
  "+^XA^RFW^LH120,20^FO0,20 ^FO30,20^A0N,30,30^FDETIQUETA DE TESTE^FS  ^FO180,50^A0N,25,25^FD@inovacode^FS^XZ|true|0|0|false";

// ---- Estado runtime de cada teste ----
type EstadoTeste = {
  status: StatusTeste;
  automatico: boolean;
  detalhe: string;
  sugestao: string;
  duracao_ms: number;
  rodando: boolean;
};

const estadoInicial = (): Record<string, EstadoTeste> => {
  const base: Record<string, EstadoTeste> = {};
  for (const t of TESTES_CATALOGO) {
    base[t.chave] = {
      status: "SKIP",
      automatico: false,
      detalhe: "",
      sugestao: "",
      duracao_ms: 0,
      rodando: false,
    };
  }
  return base;
};

type TagCali = { epc: string; rssi: number; leituras: number };

function TestadorModulo() {
  const { impressaoConfig } = useImpressaoConfig();
  const { usuario } = useAuth();
  const { isConnected, lastMessage, enviarMensagem } = useSocket();

  const hardwareId = impressaoConfig?.app_id?.trim() || "";
  const nomeUsuario = usuario?.nome || usuario?.login || "";

  const [estados, setEstados] = useState<Record<string, EstadoTeste>>(
    estadoInicial,
  );
  const [firmware, setFirmware] = useState("");
  const [infoModulo, setInfoModulo] = useState({
    mac_wifi: "",
    rede_wifi: "",
    rssi_wifi: "",
  });
  const [observacaoGeral, setObservacaoGeral] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [rodandoTudo, setRodandoTudo] = useState(false);

  // Calibração / leitura contínua
  const [lendo, setLendo] = useState(false);
  const [tagsCali, setTagsCali] = useState<TagCali[]>([]);

  // Aba de histórico
  const [tab, setTab] = useState("executar");
  const [historico, setHistorico] = useState<TesteModuloRegistro[]>([]);
  const [carregandoHist, setCarregandoHist] = useState(false);
  const [expandido, setExpandido] = useState<string | null>(null);

  // ---- Mecanismo de espera de resposta do socket ----
  // Guarda um "matcher" + resolve. Quando chega uma mensagem que casa, resolve.
  const pendenteRef = useRef<{
    match: (data: string) => boolean;
    resolve: (data: string | null) => void;
  } | null>(null);
  const lendoRef = useRef(false);

  const aguardarResposta = (
    match: (data: string) => boolean,
    timeoutMs: number,
  ): Promise<string | null> =>
    new Promise((resolve) => {
      const timer = setTimeout(() => {
        if (pendenteRef.current) {
          pendenteRef.current = null;
          resolve(null);
        }
      }, timeoutMs);
      pendenteRef.current = {
        match,
        resolve: (data) => {
          clearTimeout(timer);
          pendenteRef.current = null;
          resolve(data);
        },
      };
    });

  useEffect(() => {
    if (!lastMessage?.data) return;
    const data = String(lastMessage.data);

    // Resolve uma espera pendente (ST/INFO, LEPR/lepr+, etc.)
    if (pendenteRef.current && pendenteRef.current.match(data)) {
      pendenteRef.current.resolve(data);
    }

    // Coleta de calibração (cali+<antena>;<epc>;<rssi>) — só quando lendo.
    const lower = data.toLowerCase();
    if (lendoRef.current && lower.includes("cali+")) {
      const partes = lower.split(";");
      if (partes.length >= 3) {
        const epc = partes[1].trim().toUpperCase();
        const rssi = parseInt(partes[2].trim());
        if (epc && !isNaN(rssi)) {
          setTagsCali((prev) => {
            const idx = prev.findIndex((t) => t.epc === epc);
            if (idx !== -1) {
              const nova = [...prev];
              nova[idx] = {
                ...nova[idx],
                leituras: nova[idx].leituras + 1,
                rssi,
              };
              return nova;
            }
            return [...prev, { epc, rssi, leituras: 1 }];
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  // Para a leitura contínua ao sair da página.
  useEffect(() => {
    return () => {
      if (lendoRef.current) {
        enviarMensagem("FL");
        lendoRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (chave: string, dados: Partial<EstadoTeste>) =>
    setEstados((prev) => ({ ...prev, [chave]: { ...prev[chave], ...dados } }));

  const sugestaoDe = (chave: string) =>
    TESTES_CATALOGO.find((t) => t.chave === chave)?.sugestao || "";

  // Marca manualmente PASS/FAIL (confirmação do técnico).
  const marcar = (chave: string, status: StatusTeste) => {
    patch(chave, {
      status,
      sugestao: status === "FAIL" ? sugestaoDe(chave) : "",
    });
  };

  // ---- Runners automáticos ----

  const testarConexao = () => {
    const inicio = performance.now();
    const ok = isConnected && !!hardwareId;
    patch("conexao_ws", {
      status: ok ? "PASS" : "FAIL",
      automatico: true,
      duracao_ms: Math.round(performance.now() - inicio),
      detalhe: ok
        ? `Conectado (serial ${hardwareId}).`
        : !hardwareId
          ? "Nenhum serial/hardware_id configurado no perfil de impressão."
          : "Socket desconectado.",
      sugestao: ok ? "" : sugestaoDe("conexao_ws"),
    });
    return ok;
  };

  const testarStatusFirmware = async () => {
    const inicio = performance.now();
    patch("status_firmware", { rodando: true });
    enviarMensagem("ST");
    const resp = await aguardarResposta(
      (d) => d.includes("INFO"),
      4000,
    );
    const dur = Math.round(performance.now() - inicio);

    if (!resp) {
      patch("status_firmware", {
        status: "FAIL",
        automatico: true,
        rodando: false,
        duracao_ms: dur,
        detalhe: "Sem resposta ao comando ST (timeout 4s).",
        sugestao: sugestaoDe("status_firmware"),
      });
      patch("sinal_wifi", {
        status: "SKIP",
        detalhe: "Não avaliado — módulo não respondeu ao status.",
      });
      return false;
    }

    const msg = resp.split("|");
    const rede = msg[1]?.trim() || "";
    const versao = msg[5]?.trim() || "";
    const mac = msg[6]?.replace("WFMAC>", "").trim() || "";
    const rssi = msg[10]?.replace("RSSI>", "").trim() || "";

    setFirmware((f) => f || versao);
    setInfoModulo({ mac_wifi: mac, rede_wifi: rede, rssi_wifi: rssi });

    patch("status_firmware", {
      status: versao ? "PASS" : "FAIL",
      automatico: true,
      rodando: false,
      duracao_ms: dur,
      detalhe: versao
        ? `Firmware ${versao} • Rede ${rede || "—"} • MAC ${mac || "—"}`
        : "Módulo respondeu, mas sem versão de firmware.",
      sugestao: versao ? "" : sugestaoDe("status_firmware"),
    });

    avaliarSinal(rssi);
    return !!versao;
  };

  const avaliarSinal = (rssiStr: string) => {
    const n = parseInt(rssiStr);
    if (isNaN(n)) {
      patch("sinal_wifi", {
        status: "SKIP",
        automatico: true,
        detalhe: rssiStr
          ? `RSSI não numérico: "${rssiStr}".`
          : "Módulo não reportou RSSI.",
      });
      return;
    }
    // RSSI negativo = dBm; positivo 0-100 = porcentagem.
    let ok: boolean;
    let texto: string;
    if (n <= 0) {
      ok = n >= -78;
      texto = `${n} dBm`;
    } else {
      ok = n >= 30;
      texto = `${n}%`;
    }
    patch("sinal_wifi", {
      status: ok ? "PASS" : "FAIL",
      automatico: true,
      detalhe: `Sinal Wi-Fi: ${texto}.`,
      sugestao: ok ? "" : sugestaoDe("sinal_wifi"),
    });
  };

  const testarImpressao = () => {
    enviarMensagem(ZPL_TESTE);
    patch("impressao_teste", {
      automatico: false,
      detalhe: "Etiqueta de teste enviada. Confirme se saiu corretamente.",
    });
    alert.info?.(
      "Etiqueta de teste enviada ao módulo. Confirme o resultado (Passou/Falhou).",
    );
  };

  const testarLeitura = async () => {
    const inicio = performance.now();
    patch("leitura_tag", { rodando: true });
    enviarMensagem("LEPR");
    const resp = await aguardarResposta(
      (d) => d.toLowerCase().includes("lepr+"),
      6000,
    );
    const dur = Math.round(performance.now() - inicio);

    if (!resp) {
      patch("leitura_tag", {
        status: "FAIL",
        automatico: true,
        rodando: false,
        duracao_ms: dur,
        detalhe: "Nenhuma tag lida (timeout 6s).",
        sugestao: sugestaoDe("leitura_tag"),
      });
      return false;
    }
    const epc = resp.split("+")[1]?.trim() || "";
    patch("leitura_tag", {
      status: "PASS",
      automatico: true,
      rodando: false,
      duracao_ms: dur,
      detalhe: `Tag lida: ${epc}. Confirme se corresponde à etiqueta usada.`,
      sugestao: "",
    });
    return true;
  };

  const alternarLeituraContinua = () => {
    if (!lendo) {
      setTagsCali([]);
      enviarMensagem(`P:${impressaoConfig?.potencia_padrao ?? 2}`);
      enviarMensagem("IL");
      lendoRef.current = true;
      setLendo(true);
    } else {
      enviarMensagem("FL");
      lendoRef.current = false;
      setLendo(false);
      patch("calibracao_rf", {
        automatico: false,
        detalhe:
          tagsCali.length > 0
            ? `${tagsCali.length} tag(s) detectada(s). Confirme se a leitura está coerente.`
            : "Nenhuma tag detectada durante a leitura contínua.",
      });
    }
  };

  const rodarAutomaticos = async () => {
    if (rodandoTudo) return;
    setRodandoTudo(true);
    try {
      const conectou = testarConexao();
      if (!conectou) {
        alert.warning(
          "Módulo não conectado. Os testes automáticos que dependem do módulo vão falhar.",
        );
      }
      await testarStatusFirmware();
      await testarLeitura();
      // Impressão e calibração acionam a função mas dependem de confirmação física.
      testarImpressao();
      alert.success(
        "Testes automáticos concluídos. Confirme os itens físicos (impressão, leitura, montagem).",
      );
    } finally {
      setRodandoTudo(false);
    }
  };

  const limpar = () => {
    setEstados(estadoInicial());
    setFirmware("");
    setInfoModulo({ mac_wifi: "", rede_wifi: "", rssi_wifi: "" });
    setObservacaoGeral("");
    setTagsCali([]);
  };

  // ---- Resumo ----
  const resumo = useMemo(() => {
    const vals = Object.values(estados);
    const pass = vals.filter((v) => v.status === "PASS").length;
    const fail = vals.filter((v) => v.status === "FAIL").length;
    const skip = vals.filter((v) => v.status === "SKIP").length;
    return { pass, fail, skip, total: vals.length };
  }, [estados]);

  const resultadoGeral: "PASS" | "FAIL" =
    resumo.fail > 0 ? "FAIL" : "PASS";

  // ---- Salvar ----
  const salvar = async () => {
    if (!hardwareId) {
      alert.error(
        "Sem serial/hardware_id no perfil de impressão. Não é possível salvar o histórico por módulo.",
      );
      return;
    }
    if (!nomeUsuario) {
      alert.error("Usuário sem nome — não é possível registrar quem testou.");
      return;
    }
    if (resumo.skip === resumo.total) {
      alert.warning("Nenhum teste foi executado ainda.");
      return;
    }
    setSalvando(true);
    try {
      const itens: ItemTesteResultado[] = TESTES_CATALOGO.map((t) => {
        const e = estados[t.chave];
        return {
          chave: t.chave,
          nome: t.nome,
          categoria: t.categoria,
          tipo: t.tipo,
          status: e.status,
          automatico: e.automatico,
          detalhe: e.detalhe,
          sugestao: e.status === "FAIL" ? e.sugestao || t.sugestao : "",
          duracao_ms: e.duracao_ms,
        };
      });

      await salvarTesteModulo({
        hardware_id: hardwareId,
        id_conta: usuario?.id_conta || "",
        id_usuario: usuario?._id || "",
        nome_usuario: nomeUsuario,
        firmware_version: firmware,
        mac_wifi: infoModulo.mac_wifi,
        rede_wifi: infoModulo.rede_wifi,
        rssi_wifi: infoModulo.rssi_wifi,
        observacao_geral: observacaoGeral,
        itens,
      });

      alert.success("Teste registrado no histórico do módulo!");
      if (tab === "historico") carregarHistorico();
    } catch (err) {
      console.error("Erro ao salvar teste do módulo:", err);
      alert.error("Erro ao salvar o teste. Verifique a API de histórico.");
    } finally {
      setSalvando(false);
    }
  };

  // ---- Histórico ----
  const carregarHistorico = async () => {
    if (!hardwareId) return;
    setCarregandoHist(true);
    try {
      const dados = await buscarHistoricoTestes(hardwareId);
      setHistorico(dados);
    } catch (err) {
      console.error("Erro ao buscar histórico de testes:", err);
      alert.error("Erro ao carregar o histórico de testes.");
    } finally {
      setCarregandoHist(false);
    }
  };

  useEffect(() => {
    if (tab === "historico") carregarHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, hardwareId]);

  const fmtData = (iso?: string) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("pt-BR");
  };

  const statusBadge = (status: StatusTeste) => {
    if (status === "PASS")
      return (
        <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 gap-1">
          <Check className="h-3 w-3" /> Passou
        </Badge>
      );
    if (status === "FAIL")
      return (
        <Badge className="bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20 gap-1">
          <X className="h-3 w-3" /> Falhou
        </Badge>
      );
    return (
      <Badge variant="outline" className="text-muted-foreground gap-1">
        Pendente
      </Badge>
    );
  };

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">
            Testador de Módulo
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Teste todas as funções do módulo de impressão. Cada teste registra
            Passou/Falhou e sugere como corrigir. O histórico fica salvo por
            módulo.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="gap-1.5">
            <Cpu className="h-3.5 w-3.5" />
            {hardwareId || "sem serial"}
          </Badge>
          <Badge
            variant="outline"
            className={
              isConnected
                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 gap-1.5"
                : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20 gap-1.5"
            }
          >
            <Wifi className="h-3.5 w-3.5" />
            {isConnected ? "Conectado" : "Desconectado"}
          </Badge>
        </div>
      </div>

      {!hardwareId && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Sem serial de hardware configurado</p>
              <p className="text-muted-foreground text-xs mt-0.5">
                Defina o hardware_id (serial) no perfil de impressão em uso. Sem
                ele não é possível conectar ao módulo nem salvar o histórico por
                módulo.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="executar" className="gap-1.5">
            <Play className="h-3.5 w-3.5" /> Executar Teste
          </TabsTrigger>
          <TabsTrigger value="historico" className="gap-1.5">
            <History className="h-3.5 w-3.5" /> Histórico
          </TabsTrigger>
        </TabsList>

        {/* ===================== EXECUTAR ===================== */}
        <TabsContent value="executar" className="flex flex-col gap-4 mt-4">
          {/* Identificação */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">
                  Identificação do teste
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="grid gap-1.5">
                <Label className="text-xs text-muted-foreground">
                  Módulo (serial)
                </Label>
                <Input disabled value={hardwareId || "—"} />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs text-muted-foreground">
                  Testado por
                </Label>
                <Input disabled value={nomeUsuario || "—"} />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs text-muted-foreground">
                  Versão do firmware
                </Label>
                <Input
                  value={firmware}
                  onChange={(e) => setFirmware(e.target.value)}
                  placeholder="Preenchido pelo status ou manual"
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs text-muted-foreground">
                  Rede / RSSI
                </Label>
                <Input
                  disabled
                  value={
                    infoModulo.rede_wifi || infoModulo.rssi_wifi
                      ? `${infoModulo.rede_wifi || "—"} • ${infoModulo.rssi_wifi || "—"}`
                      : "—"
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Ações + resumo */}
          <Card>
            <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={rodarAutomaticos} disabled={rodandoTudo}>
                  {rodandoTudo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  Rodar testes automáticos
                </Button>
                <Button variant="outline" onClick={limpar} disabled={rodandoTudo}>
                  <RefreshCw className="h-4 w-4" />
                  Limpar
                </Button>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="flex items-center gap-1 text-emerald-600">
                  <CheckCircle2 className="h-4 w-4" /> {resumo.pass}
                </span>
                <span className="flex items-center gap-1 text-red-600">
                  <XCircle className="h-4 w-4" /> {resumo.fail}
                </span>
                <span className="text-muted-foreground">
                  {resumo.skip} pendente(s)
                </span>
                <Separator orientation="vertical" className="h-5" />
                <Badge
                  className={
                    resultadoGeral === "PASS"
                      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20"
                      : "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20"
                  }
                >
                  Geral: {resultadoGeral === "PASS" ? "Aprovado" : "Reprovado"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Lista de testes */}
          <div className="flex flex-col gap-3">
            {TESTES_CATALOGO.map((t) => {
              const e = estados[t.chave];
              const acionavel = t.tipo === "auto" || t.tipo === "hibrido";
              return (
                <Card key={t.chave}>
                  <CardContent className="p-4 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">{t.nome}</span>
                          <Badge variant="secondary" className="text-[10px]">
                            {t.categoria}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">
                            {t.tipo === "auto"
                              ? "automático"
                              : t.tipo === "hibrido"
                                ? "híbrido"
                                : "manual"}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t.descricao}
                        </p>
                        {t.instrucao && (
                          <p className="text-xs text-muted-foreground mt-1 italic">
                            {t.instrucao}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0">{statusBadge(e.status)}</div>
                    </div>

                    {/* Ações do teste */}
                    <div className="flex flex-wrap items-center gap-2">
                      {t.chave === "status_firmware" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={testarStatusFirmware}
                          disabled={e.rodando}
                        >
                          {e.rodando ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Play className="h-3.5 w-3.5" />
                          )}
                          Coletar status
                        </Button>
                      )}
                      {t.chave === "conexao_ws" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={testarConexao}
                        >
                          <Play className="h-3.5 w-3.5" /> Verificar
                        </Button>
                      )}
                      {t.chave === "impressao_teste" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={testarImpressao}
                        >
                          <Play className="h-3.5 w-3.5" /> Imprimir teste
                        </Button>
                      )}
                      {t.chave === "leitura_tag" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={testarLeitura}
                          disabled={e.rodando}
                        >
                          {e.rodando ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Play className="h-3.5 w-3.5" />
                          )}
                          Ler tag
                        </Button>
                      )}
                      {t.chave === "calibracao_rf" && (
                        <Button
                          size="sm"
                          variant={lendo ? "destructive" : "outline"}
                          onClick={alternarLeituraContinua}
                        >
                          {lendo ? (
                            <>
                              <Square className="h-3.5 w-3.5" /> Parar (
                              {tagsCali.length})
                            </>
                          ) : (
                            <>
                              <Play className="h-3.5 w-3.5" /> Iniciar leitura
                            </>
                          )}
                        </Button>
                      )}

                      <Separator orientation="vertical" className="h-6" />

                      <Button
                        size="sm"
                        variant={e.status === "PASS" ? "default" : "outline"}
                        className={
                          e.status === "PASS"
                            ? "bg-emerald-600 hover:bg-emerald-700"
                            : ""
                        }
                        onClick={() => marcar(t.chave, "PASS")}
                      >
                        <Check className="h-3.5 w-3.5" /> Passou
                      </Button>
                      <Button
                        size="sm"
                        variant={
                          e.status === "FAIL" ? "destructive" : "outline"
                        }
                        onClick={() => marcar(t.chave, "FAIL")}
                      >
                        <X className="h-3.5 w-3.5" /> Falhou
                      </Button>
                      {!acionavel && (
                        <span className="text-[11px] text-muted-foreground">
                          verificação manual
                        </span>
                      )}
                    </div>

                    {/* Observação */}
                    <Textarea
                      value={e.detalhe}
                      onChange={(ev) =>
                        patch(t.chave, { detalhe: ev.target.value })
                      }
                      placeholder="Observação (opcional)…"
                      className="min-h-[60px] text-xs"
                    />

                    {/* Sugestão em caso de falha */}
                    {e.status === "FAIL" && (
                      <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                        <Lightbulb className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-xs">
                          <p className="font-medium text-amber-700 dark:text-amber-400">
                            Como corrigir
                          </p>
                          <p className="text-muted-foreground mt-0.5">
                            {e.sugestao || t.sugestao}
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Observação geral + salvar */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">
                Observação geral
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Textarea
                value={observacaoGeral}
                onChange={(e) => setObservacaoGeral(e.target.value)}
                placeholder="Observações gerais do equipamento, o que foi feito na construção, bloqueador, etc."
                className="min-h-[80px] text-sm"
              />
              <div className="flex justify-end">
                <Button onClick={salvar} disabled={salvando}>
                  {salvando ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Salvar teste no histórico
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===================== HISTÓRICO ===================== */}
        <TabsContent value="historico" className="flex flex-col gap-4 mt-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Histórico de testes do módulo{" "}
              <span className="font-medium">{hardwareId || "—"}</span>
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={carregarHistorico}
              disabled={carregandoHist || !hardwareId}
            >
              <RefreshCw
                className={`h-4 w-4 ${carregandoHist ? "animate-spin" : ""}`}
              />
              Atualizar
            </Button>
          </div>

          {historico.length === 0 && !carregandoHist ? (
            <Card>
              <CardContent className="py-12 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                <History className="h-8 w-8 opacity-40" />
                <p className="text-sm">
                  Nenhum teste registrado para este módulo ainda.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="flex flex-col gap-3">
              {historico.map((h) => {
                const aberto = expandido === h._id;
                return (
                  <Card key={h._id}>
                    <CardContent className="p-4">
                      <button
                        className="w-full flex items-center justify-between gap-3 text-left"
                        onClick={() =>
                          setExpandido(aberto ? null : h._id || null)
                        }
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <Badge
                            className={
                              h.resultado_geral === "PASS"
                                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20"
                                : "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20"
                            }
                          >
                            {h.resultado_geral === "PASS"
                              ? "Aprovado"
                              : "Reprovado"}
                          </Badge>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {fmtData(h.createdAt)} • {h.nome_usuario}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {h.total_pass} ok · {h.total_fail} falha(s) ·{" "}
                              {h.total_skip} pendente(s)
                              {h.firmware_version
                                ? ` · fw ${h.firmware_version}`
                                : ""}
                            </p>
                          </div>
                        </div>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 transition-transform ${
                            aberto ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      {aberto && (
                        <div className="mt-3 flex flex-col gap-2">
                          {h.observacao_geral && (
                            <p className="text-xs text-muted-foreground border-l-2 border-muted pl-2">
                              {h.observacao_geral}
                            </p>
                          )}
                          <Separator />
                          {h.itens.map((it) => (
                            <div
                              key={it.chave}
                              className="flex flex-col gap-1 py-1"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-medium">
                                  {it.nome}
                                </span>
                                {statusBadge(it.status)}
                              </div>
                              {it.detalhe && (
                                <p className="text-[11px] text-muted-foreground">
                                  {it.detalhe}
                                </p>
                              )}
                              {it.status === "FAIL" && it.sugestao && (
                                <p className="text-[11px] text-amber-700 dark:text-amber-400 flex items-start gap-1">
                                  <Lightbulb className="h-3 w-3 mt-0.5 shrink-0" />
                                  {it.sugestao}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TestadorModulo;
